import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Users, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { AuthShell, PrimaryButton, OutlineButton, AUTH_SUBTLE, AUTH_BLUE } from './authShared';

function AcceptInvite({ user, onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('checking'); // 'checking', 'ready', 'accepted', 'error', 'login_required', 'wrong_account'
  const [isAccepting, setIsAccepting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [inviteDetails, setInviteDetails] = useState(null);
  const [autoAccepting, setAutoAccepting] = useState(false);

  const shouldAutoAccept = localStorage.getItem('autoAcceptInvite') === 'true';

  useEffect(() => {
    const verifyAndPossiblyAutoAccept = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage('Invalid invitation link. Please check your email for the correct link.');
        return;
      }
      try {
        const response = await apiService.verifyTeamInvite(token);
        setInviteDetails(response.data);
        if (!user) {
          setStatus('login_required');
          return;
        }
        const invitedEmail = response.data.email?.toLowerCase();
        const loggedInEmail = user.email?.toLowerCase();
        if (invitedEmail && loggedInEmail && invitedEmail !== loggedInEmail) {
          setStatus('wrong_account');
          localStorage.removeItem('autoAcceptInvite');
          return;
        }
        if (shouldAutoAccept) {
          localStorage.removeItem('autoAcceptInvite');
          setAutoAccepting(true);
          try {
            await apiService.acceptTeamInvite(token, user.id);
            setStatus('accepted');
            toast.success('You have joined the team!');
            setTimeout(() => { window.location.href = '/dashboard'; }, 2000);
            return;
          } catch (acceptError) {
            console.error('Auto-accept failed:', acceptError);
          }
          setAutoAccepting(false);
        }
        setStatus('ready');
      } catch (error) {
        setStatus('error');
        localStorage.removeItem('autoAcceptInvite');
        setErrorMessage(error.response?.data?.detail || 'Failed to verify invitation. The link may be expired or invalid.');
      }
    };
    verifyAndPossiblyAutoAccept();
  }, [token, user, shouldAutoAccept, navigate]);

  const handleAcceptInvite = async () => {
    setIsAccepting(true);
    try {
      await apiService.acceptTeamInvite(token, user.id);
      setStatus('accepted');
      toast.success('You have joined the team!');
      setTimeout(() => { window.location.href = '/dashboard'; }, 2000);
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 'Failed to accept invitation. The link may be expired or invalid.');
      toast.error('Failed to accept invitation');
    } finally {
      setIsAccepting(false);
    }
  };

  const handleLoginFirst = () => {
    localStorage.setItem('pendingInviteToken', token);
    navigate('/login');
  };

  const handleSignupFirst = () => {
    localStorage.setItem('pendingInviteToken', token);
    navigate('/team-signup');
  };

  const handleSwitchAccount = () => {
    localStorage.removeItem('user');
    localStorage.setItem('pendingInviteToken', token);
    window.location.href = '/login';
  };

  const iconWrap = (bg, node) => (
    <div className="w-14 h-14 rounded-md flex items-center justify-center mx-auto mb-4" style={{ background: bg }}>{node}</div>
  );

  return (
    <AuthShell backTo={null}>
      <div className="text-center mb-6">
        {(status === 'checking' || autoAccepting) && iconWrap('#EEF2FB', <Loader2 className="w-7 h-7 animate-spin" style={{ color: AUTH_BLUE }} />)}
        {status === 'ready' && !autoAccepting && iconWrap('#EEF2FB', <Users className="w-7 h-7" style={{ color: AUTH_BLUE }} />)}
        {status === 'login_required' && iconWrap('#FEF3E2', <Users className="w-7 h-7" style={{ color: '#B45309' }} />)}
        {status === 'accepted' && iconWrap('#E7F6EF', <CheckCircle className="w-7 h-7" style={{ color: '#059669' }} />)}
        {status === 'error' && iconWrap('#FDECEC', <XCircle className="w-7 h-7" style={{ color: '#DC2626' }} />)}
        {status === 'wrong_account' && iconWrap('#FEF0E7', <XCircle className="w-7 h-7" style={{ color: '#C2410C' }} />)}

        <h1 className="text-2xl">
          {(status === 'checking' || autoAccepting) && (autoAccepting ? 'Joining team...' : 'Verifying invitation...')}
          {status === 'ready' && !autoAccepting && 'Team invitation'}
          {status === 'login_required' && 'Sign in required'}
          {status === 'accepted' && 'Welcome to the team'}
          {status === 'error' && 'Invitation error'}
          {status === 'wrong_account' && 'Wrong account'}
        </h1>
        <p className="mt-2 text-[15px]" style={{ color: AUTH_SUBTLE }}>
          {(status === 'checking' || autoAccepting) && (autoAccepting ? 'Please wait while we add you to the team...' : 'Please wait while we verify your invitation.')}
          {status === 'ready' && !autoAccepting && 'You have been invited to join a team on DrawReady.'}
          {status === 'login_required' && 'Please sign in or create an account to accept this invitation.'}
          {status === 'accepted' && 'You have successfully joined the team. Redirecting to dashboard...'}
          {status === 'error' && errorMessage}
          {status === 'wrong_account' && `This invitation was sent to ${inviteDetails?.email}. Please sign in with that account.`}
        </p>
      </div>

      {status === 'ready' && (
        <div className="space-y-4">
          <div className="rounded-md border p-4" style={{ borderColor: '#EAECF0', background: '#F8FAFC' }}>
            <p className="text-sm" style={{ color: AUTH_SUBTLE }}>Signed in as: <span className="font-semibold" style={{ color: '#1D2939' }}>{user?.email}</span></p>
          </div>
          <div className="rounded-md border p-4" style={{ borderColor: '#D6E4FF', background: '#EEF2FB' }}>
            <p className="text-sm" style={{ color: '#1D2939' }}>By accepting this invitation, you'll gain access to view and manage projects for this team based on your assigned role.</p>
          </div>
          <div className="flex gap-3">
            <OutlineButton onClick={() => navigate('/dashboard')} className="flex-1">Decline</OutlineButton>
            <PrimaryButton onClick={handleAcceptInvite} disabled={isAccepting} className="flex-1 flex items-center justify-center gap-2">
              {isAccepting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Accepting...</>) : (<>Accept Invitation <ArrowRight className="w-4 h-4" /></>)}
            </PrimaryButton>
          </div>
        </div>
      )}

      {status === 'login_required' && (
        <div className="space-y-4">
          <div className="rounded-md border p-4" style={{ borderColor: '#BBF0D2', background: '#E7F6EF' }}>
            <p className="text-sm" style={{ color: '#065F46' }}><strong>No payment required!</strong> You've been invited to join a team. Create a free account or sign in to accept the invitation.</p>
          </div>
          <PrimaryButton onClick={handleLoginFirst} className="flex items-center justify-center gap-2">Sign In <ArrowRight className="w-4 h-4" /></PrimaryButton>
          <OutlineButton onClick={handleSignupFirst}>Create Free Account</OutlineButton>
        </div>
      )}

      {status === 'accepted' && (
        <div className="text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: AUTH_BLUE }} />
          <p className="text-sm mt-2" style={{ color: AUTH_SUBTLE }}>Redirecting to dashboard...</p>
        </div>
      )}

      {status === 'error' && (
        <PrimaryButton onClick={() => navigate('/dashboard')}>Go to Dashboard</PrimaryButton>
      )}

      {status === 'wrong_account' && (
        <div className="space-y-4">
          <div className="rounded-md border p-4" style={{ borderColor: '#FBD9BE', background: '#FEF0E7' }}>
            <p className="text-sm" style={{ color: '#9A3412' }}>You are currently signed in as <strong>{user?.email}</strong>, but this invitation was sent to <strong>{inviteDetails?.email}</strong>.</p>
          </div>
          <PrimaryButton onClick={handleSwitchAccount} className="flex items-center justify-center gap-2">Sign Out & Use Correct Account <ArrowRight className="w-4 h-4" /></PrimaryButton>
          <OutlineButton onClick={() => navigate('/dashboard')}>Go to Dashboard</OutlineButton>
        </div>
      )}
    </AuthShell>
  );
}

export default AcceptInvite;
